import React from "react";
import { Link } from "react-router-dom";
import { Heart, Sparkles, HandPlatter, Gem } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import Seo from "@/components/Seo";

const VALUES = [
  { icon: Sparkles, title: "Quality First", text: "We never compromise on ingredients or execution. Excellence is the baseline." },
  { icon: Heart, title: "Genuine Hospitality", text: "We treat your guests like our own family — warmth is our signature ingredient." },
  { icon: Gem, title: "Creativity", text: "Bold, memorable menus that surprise and delight every palate." },
  { icon: HandPlatter, title: "Personalized Service", text: "Your event, your vision. Every detail is tailored uniquely to you." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <Seo
        title="About Us | K.O CHEF & CATERING — Southwest Florida Catering Company"
        description="Learn the story behind K.O CHEF & CATERING LLC — a premium catering & private chef company serving Naples, Bonita Springs and Fort Myers with fresh ingredients, creativity and genuine hospitality."
        path="/about"
      />
      <PageHero overline="Our Story" title="The Heart Behind K.O Chef & Catering"
        subtitle="Where flavor meets unforgettable experiences."
        image="https://images.unsplash.com/photo-1748778292263-1d503e8ae93d?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000" />

      <section className="py-24 md:py-32">
        <div className="container-lux grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <img src="https://images.unsplash.com/photo-1663530761401-15eefb544889?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Plating" className="w-full h-[520px] object-cover" />
          </Reveal>
          <Reveal delay={0.15}>
            <p className="overline mb-4">Born in Southwest Florida</p>
            <h2 className="text-4xl font-light text-white mb-6 leading-tight">A passion for food, a devotion to people.</h2>
            <div className="space-y-5 text-white/60 leading-relaxed">
              <p>K.O CHEF & CATERING LLC was founded on a simple belief: that extraordinary food and genuine hospitality can turn any gathering into a lasting memory. Serving Naples, Bonita Springs, Fort Myers and the surrounding communities, we bring restaurant-caliber cuisine directly to your event.</p>
              <p>Our chefs source the freshest local ingredients daily, crafting menus that balance timeless technique with creative flair. From intimate private dinners to weddings for hundreds, every plate is prepared with the same uncompromising care.</p>
              <p>But great food is only half the story. We pride ourselves on professionalism, seamless service, and the kind of personalized attention that makes hosts feel like guests at their own events.</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-24 bg-background">
        <div className="container-lux">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <Reveal><p className="overline mb-4">What We Stand For</p></Reveal>
            <Reveal delay={0.1}><h2 className="text-4xl md:text-5xl font-light text-white">Our values on every plate.</h2></Reveal>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08}>
                <div className="bg-background p-8 h-full">
                  <v.icon className="w-8 h-8 text-gold mb-6" strokeWidth={1.3} />
                  <h3 className="text-xl font-light text-white mb-3">{v.title}</h3>
                  <p className="text-sm text-white/55 leading-relaxed">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 text-center">
        <div className="container-lux">
          <Reveal><h2 className="text-4xl font-light text-white mb-8">Taste the difference for yourself.</h2></Reveal>
          <Reveal delay={0.1}><Link to="/request-a-quote" className="btn-gold" data-testid="about-cta">Request a Quote</Link></Reveal>
        </div>
      </section>
    </div>
  );
}
